<template>
  <div>
    <v-dialog
      v-model="availabilityDialog"
      max-width="600px"
      :fullscreen="xs"
    >
      <global-card
        :title="(editMode) ? t('smpw.edit_availability') : t('smpw.submit_availability')"
        closable
        @close="cancel()"
      >
        <loading-spinner
          v-if="(savingCount > 0) || (loadingCount > 0)"
          :message="t('states.loading')"
        />
        <v-alert
          v-if="user.requires_linked_user_for_shift"
          type="warning"
          class="mb-2"
          :text="t('shift.warning.requires_linked_user')"
        >
          <ul>
            <li
              v-for="(u, index) in user.parent_users"
              :key="index"
              class="text-body-large"
            >
              {{ u.full_name }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-if="savingError"
          class="mb-2"
          type="error"
          :text="t('errors.failed_to_save_availability', { reason: savingError })"
        />
        <v-alert
          v-if="loadingError"
          class="mb-2"
          type="error"
          :text="t('errors.failed_to_load_availability', { reason: loadingError })"
        />

        <div v-if="selectedDate">
          <div
            v-if="!loadingCount"
            class="text-body-large font-weight-semibold"
          >
            {{ toLongDateWithDay(selectedDate) }}
          </div>
          <form
            v-if="canSave"
            @submit.stop.prevent="handleSubmit()"
          >
            <v-alert
              v-if="assignedShiftsThisWeek >= user.shifts_per_week"
              type="warning"
            >
              {{ t('shift.already_assigned_week', { count: assignedShiftsThisWeek }, assignedShiftsThisWeek) }}
              <span v-if="maxShiftsPerWeek > props.user.shifts_per_week">
                {{ t('shift.already_assigned_increase_limit_week', { count: assignedShiftsThisWeek }, assignedShiftsThisWeek) }}
              </span>
            </v-alert>
            <div v-if="anyTimeSlotsAvailable">
              <div
                class="mt-3"
              >
                <radio-button-group
                  v-model="lastMinute"
                  :label="t('shift.can_cover_last_minute')"
                  inline
                  :class="lastMinuteInvalid ? 'mb-3' : ''"
                  :error="lastMinuteInvalid"
                  :error-messages="lastMinuteInvalid ? [lastMinuteInvalidMessage] : undefined"
                  :items="[{
                    text: t('global.yes'),
                    value: true,
                  }, {
                    text: t('global.no'),
                    value: false,
                  }]"
                />
                <v-divider class="mb-3" />
                <radio-button-group
                  v-model="shiftsPerDay"
                  :label="t('shift.how_many')"
                  inline
                  :items="shiftsPerDayOptions"
                />

                <v-alert
                  v-if="assignedShiftsThisDate >= shiftsPerDay"
                  type="warning"
                  class="text-body-large"
                >
                  {{ t('shift.already_assigned_day', { count: assignedShiftsThisDate }, assignedShiftsThisDate) }}
                  <span v-if="maxShiftsPerDay > shiftsPerDay">
                    {{ t('shift.already_assigned_increase_limit_day', { count: assignedShiftsThisDate }, assignedShiftsThisDate) }}
                  </span>
                </v-alert>
                <v-divider />
                <div
                  v-if="shiftTags.length > 0"
                  class="my-2"
                >
                  <span class="text-body-large">{{ t('shift.tagged_as') }} </span>
                  <div
                    v-for="(tag, index) in uniqueShiftTags"
                    :key="index"
                    class="mt-2"
                  >
                    <shift-tag
                      :shift-tag="tag"
                      size="large"
                    />
                  </div>
                  <v-divider />
                </div>

                <table>
                  <thead>
                    <tr>
                      <th>
                        <div class="float-left mt-3 text-font">
                          {{ t('date.time', 2) }}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(slot, index) in availableSlotOptions"
                      :key="index"
                    >
                      <th class="d-flex flex-column flex-sm-row justify-space-between align-sm-center">
                        <v-checkbox
                          v-model="selectedTimeBands"
                          :value="slot.value"
                          :error="selectedTimeBands.length === 0 && formDirty"
                          :disabled="slot.disabled"
                          class="me-2"
                          @update:model-value="formDirty = true"
                        >
                          <template
                            #label
                          >
                            <span class="text-body-large">{{ slot.label }} - {{ slot.band.name }}</span>
                          </template>
                        </v-checkbox>
                        <div class="d-flex flex-row">
                          <shift-tag
                            v-for="(tag, i) in slot.tags"
                            :key="i"
                            class="me-1 mb-1"
                            :shift-tag="tag"
                          />
                        </div>
                      </th>
                    </tr>
                  </tbody>
                </table>
                <div
                  v-if="selectedTimeBands.length === 0 && formDirty"
                  class="text-error mt-2"
                >
                  {{ t('shift.select_one_slot') }}
                </div>
                <table
                  v-if="getSetting('allow_location_selection') && selectedTimeBands.length > 0"
                  style="min-width: 50%"
                >
                  <thead>
                    <tr>
                      <th>
                        <div class="float-left mt-3 text-font">
                          {{ t('location.title', 2) }}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>
                        <v-checkbox
                          v-if="availableLocations.length > 2"
                          v-model="allLocationsSelected"
                          :indeterminate="someLocationsSelected"
                          :error="!selectedLocationsValid"
                          @update:model-value="toggleAllLocations"
                        >
                          <template #label>
                            <span class="text-body-large">
                              {{ allLocationsSelected ? t('location.deselect_all') : t('location.select') }}
                            </span>
                          </template>
                        </v-checkbox>
                      </th>
                    </tr>

                    <tr
                      v-for="(location, index) in availableLocationOptions"
                      :key="index"
                    >
                      <th class="d-flex flex-column flex-sm-row justify-space-between">
                        <v-checkbox
                          v-model="selectedLocations"
                          :error="!selectedLocationsValid || selectedLocations.length === 0"
                          :value="location.id"
                          :disabled="location.disabled"
                          class="me-2"
                        >
                          <template #label>
                            <span class="text-body-large">{{ location.name }}</span>
                          </template>
                        </v-checkbox>
                        <div class="d-flex flex-row">
                          <shift-tag
                            v-for="(tag, i) in location.tags"
                            :key="i"
                            class="me-1 mb-1"
                            :shift-tag="tag"
                          />
                        </div>
                      </th>
                    </tr>
                  </tbody>
                </table>
                <div
                  v-if="selectedTimeBands.length && (!selectedLocationsValid || selectedLocations.length === 0)"
                  class="text-error mt-2"
                >
                  {{ t('location.at_least_one') }}
                </div>

                <v-select
                  v-model="recurringWeeks"
                  :label="t('shift.apply_to')"
                  class="mt-5"
                  :items="recurringOptions"
                  item-title="text"
                  item-value="value"
                />
              </div>
            </div>
            <v-alert
              v-else
              type="warning"
              class="mt-3"
              :text="t('shift.no_slots_available')"
            />
          </form>
        </div>
        <v-alert
          v-else
          type="error"
          class="mt-3"
          :text="t('shift.no_date_selected')"
        />

        <template #actions>
          <v-btn
            v-if="editMode && anyTimeSlotsAvailable"
            color="error"
            :disabled="!canSave"
            :text="t('actions.delete')"
            size="default"
            @click="deleteAvailability = true"
          />
          <v-btn
            :text="t('actions.close')"
            color="dark-grey"
            size="default"
            @click="cancel()"
          />
          <v-btn
            v-if="anyTimeSlotsAvailable && selectedDate"
            color="primary"
            :disabled="!canSave || !selectedLocationsValid || !selectedTimeBandsValid || lastMinuteInvalid"
            :text="editMode ? t('actions.update') : t('actions.request')"
            size="default"
            @click="handleSubmit(false)"
          />
          <v-btn
            v-if="!editMode && anyTimeSlotsAvailable && selectedDate && canBookForSpouse"
            color="secondary"
            :text="t('actions.request_spouse')"
            :disabled="!canSave || !selectedLocationsValid || !selectedTimeBandsValid || lastMinuteInvalid"
            size="default"
            @click="handleSubmit(true)"
          />
        </template>
      </global-card>
    </v-dialog>
    <delete-availability-dialog
      v-if="selectedDate"
      v-model="deleteAvailability"
      :date="selectedDate"
      :user="user"
      :edit-mode="editMode"
      @availability:deleted="onAvailabilityDeleted"
    />
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import {
  computed,
  ref,
  watch,
} from 'vue';
// @ts-nocheck
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import {
  useAuth,
  useDate,
  useSettings,
} from '@/composables';
import { useErrorStore } from '@/stores/errors';
import ShiftTag from '../ShiftTag.vue';
import DeleteAvailabilityDialog from './DeleteAvailabilityDialog.vue';

const props = withDefaults(defineProps<{
  user: App.Models.User;
  dontCheckTraining?: boolean;
  preSelectedSlots?: Array<PreselectedSlot>;
}>(), {
  dontCheckTraining: () => false,
  preSelectedSlots: () => [],
});

const emit = defineEmits(['availability:updated']);

const { t } = useI18n();

const { xs } = useDisplay();
const formDirty = ref(false);
const availabilityDialog = ref(false);
const deleteAvailability = ref(false);
const { showSnackMessage, handleError } = useErrorStore();

const {
  addToDate,
  toUniversalDate,
  startOf,
  toShortTime,
  isSameOrBefore,
  isSameOrAfter,
  getAsDate,
  planningDates,
  getAsValidDateOrNull,
  toLongDateWithDay,
} = useDate();
const { authUser } = useAuth();
const { getSetting } = useSettings();

/**
 * A row from `/api/shifts/tags`, which flattens each shift's tags into one entry per
 * (shift, tag) pair - see `ShiftShiftTagApiController::index`.
 */
type ShiftTagRow = App.ApiResources.BasicShiftTagResource & {
  tag_id: number;
  shift_id: number;
  location_id: number | null;
  date: string;
  begins: string;
};

/** A time slot in the picker: the plan slot, plus the state this dialog paints onto it. */
type SelectableSlot = Omit<App.ApiResources.ShiftPlanSlotResource, 'locations' | 'band'> & {
  locations: LocationOption[];
  band: App.Models.ShiftTimeBand;
  value?: number;
  disabled?: boolean;
  tags?: ShiftTagRow[];
  label?: string;
};

/**
 * A shift the publisher is already attached to. `band` and `location` are eager-loaded and
 * `pivot.status` carries their availability - see `PublisherShiftAvailabilityApiController::index`.
 */
type AvailabilityShift = Omit<App.Models.Shift, 'band'> & {
  band: App.Models.ShiftTimeBand;
  pivot: { status: string };
};

const assignedShiftsThisDate = ref(0);
const assignedShiftsThisWeek = ref(0);
const selectedDate = ref<Date | null>(null);
const shiftTags = ref<ShiftTagRow[]>([]);
const availableSlots = ref<SelectableSlot[]>([]);
const availabilityResponse = ref<AvailabilityShift[]>([]);
const selectedTimeBands = ref<number[]>([]);
const selectedTimeBandsValid = computed(() => selectedTimeBands.value?.length > 0);
/** A shift need not have a location, so an entry here can be `undefined`. */
const assignedLocations = ref<Array<number | undefined>>([]);
const availableLocations = ref<LocationOption[]>([]);
const selectedLocations = ref<Array<number | undefined>>([]);
const selectedLocationsValid = computed(() => selectedLocations.value?.length > 0);
const lastMinute = ref(true);
const shiftsPerDay = ref(1);
const allLocationsSelected = ref(false);
const someLocationsSelected = ref(false);
const loadingCount = ref(0);
const loadingError = ref<any>(null);
const savingCount = ref(0);
const savingError = ref<any>(null);
const recurringWeeks = ref(0);

const lastMinuteInvalidMessage = t('shift.last_minute_warning');
const editMode = computed(() => {
  return availabilityResponse.value.length > 0;
});
function onAvailabilityDeleted() {
  availabilityDialog.value = false;
  emit('availability:updated', selectedDate.value);
}

const maxShiftsPerDay = computed(() => getSetting('max_shifts_per_day') ?? 0);
const maxShiftsPerWeek = computed(() => getSetting('max_shifts_per_week') ?? 0);
const canSave = computed(() => (loadingCount.value === 0) && (savingCount.value === 0) && !loadingError.value);
const recurringOptions = computed(() => {
  const recurrantOptions = [
    {
      value: 0,
      text: t('date.just_this_week'),
    },
  ];
  const times = getSetting('planning_weeks') ?? 0;
  const maxDate = getAsDate(planningDates().end);
  const fromDate = planningDates().start;
  const from: Date = (!selectedDate.value || fromDate > selectedDate.value)
    ? fromDate
    : selectedDate.value;
  let endDate: any = null;
  for (let i = 1; i < times; i += 1) {
    endDate = addToDate(from, i, 'weeks');
    if (endDate < maxDate) {
      recurrantOptions.push({
        value: i,
        text: t('shift.available.recurring_option', {
          count: i,
          end_date: endDate.format('dddd, MMMM Do'),
        }, i),
      });
    }
  }
  return recurrantOptions;
});

const isSelectedDateLastMinute = computed(() => {
  const today = startOf(undefined);
  const shiftDate = startOf(selectedDate.value);
  const diffDays = shiftDate.diff(today, 'days');
  const lastMinuteDays = getSetting('last_minute_days') ?? 0;
  return (diffDays < lastMinuteDays);
});

const lastMinuteInvalid = computed(() => !lastMinute.value && isSelectedDateLastMinute.value);

function didSelectionChange(newSelection: unknown[], oldSelection: unknown[]): boolean {
  const newValue = newSelection.slice(0).sort();
  const oldValue = oldSelection.slice(0).sort();
  return !((newValue.length === oldValue.length) && newValue.every((value, index) => value === oldValue[index]));
}

function toggleAllLocations() {
  if (allLocationsSelected.value) {
    selectedLocations.value = availableLocations.value.map((location) => location.id);
  }
  else {
    selectedLocations.value = assignedLocations.value;
  }
}

function isLocationAssigned(locationId: number): boolean {
  return assignedLocations.value.includes(locationId);
}
/** The first row for each distinct tag id among `rows`, preserving their order. */
function distinctByTagId(rows: ShiftTagRow[]): ShiftTagRow[] {
  const seen = new Map<number, ShiftTagRow>();
  rows.forEach((row) => {
    if (!seen.has(row.id)) {
      seen.set(row.id, row);
    }
  });
  return Array.from(seen.values());
}

function getShiftTagsForLocation(locationId: number): ShiftTagRow[] {
  return distinctByTagId(shiftTags.value.filter((row) => row.location_id === locationId));
}
function getShiftTagsForTime(begins: string): ShiftTagRow[] {
  return distinctByTagId(shiftTags.value.filter((row) => row.begins === begins));
}

const assignedTimeBands = computed(() => {
  const returnBands: Array<number | null> = [];
  const shifts = availabilityResponse.value.filter((shift) => shift.pivot?.status === 'assigned');
  shifts.forEach((shift) => {
    returnBands.push(shift.shift_time_band_id);
  });
  return returnBands;
});

const cancelledTimeBands = computed(() => {
  const returnBands: Array<number | null> = [];
  const shifts = availabilityResponse.value.filter((shift) => shift.pivot?.status === 'cancelled');
  shifts.forEach((shift) => {
    returnBands.push(shift.shift_time_band_id);
  });
  return returnBands;
});

function isTimeBandAssignedOrCancelled(timeBandId: number): boolean {
  return assignedTimeBands.value.includes(timeBandId) || cancelledTimeBands.value.includes(timeBandId);
}

const shiftsPerDayOptions = computed(() => Array.from(new Array(maxShiftsPerDay.value)).map((x, i) => ({ text: (i + 1), value: (i + 1) })));
const anyTimeSlotsAvailable = computed(() => availableSlots.value.some((slot) => !slot.disabled));
const canBookForSpouse = computed(() => props.user.spouse?.active && props.user.spouse?.training_completed);
const availableSlotOptions = computed(() => availableSlots.value.map((slot) => {
  slot.value = slot.shift_time_band_id;
  slot.disabled = (isTimeBandAssignedOrCancelled(slot.shift_time_band_id) || isSameOrBefore(`${toUniversalDate(selectedDate.value)} ${slot.begins}`));
  slot.tags = getShiftTagsForTime(slot.begins);
  slot.label = `${toShortTime(slot.begins)} - ${toShortTime(slot.ends)}`;
  return slot;
}));

interface LocationOptionPivot {
  created_at: string;
  location_id: number;
  shift_plan_slot_id: number;
  updated_at: string;
}
interface LocationOption {
  id: number;
  name: string;
  code: string;
  publishers_per_shift: number;
  min_brothers: number;
  details: string;
  store_id: number;
  active: boolean;
  created_at: string;
  created_by: null;
  updated_at: string;
  updated_by: null;
  hero_image_id: null;
  pivot: LocationOptionPivot;
  disabled: boolean;
  tags: Array<any>;
}

// Left to inference: under the non-strict config the inferred element type and
// `LocationOption` are not quite the same, and the inferred one is what the template needs.
const availableLocationOptions = computed(() => {
  const tempLocations = availableLocations.value;

  return tempLocations.sort((a, b) => a.name.localeCompare(b.name)).map((location) => {
    location.tags = getShiftTagsForLocation(location.id);
    return location;
  });
});

/*
  Get unique shift tags from the shiftTags collection
  otherwise we end up displaying every permutation of
  location, slots and tags.
*/
const uniqueShiftTags = computed(() => {
  const uniqueTags: Record<number, ShiftTagRow> = {};
  shiftTags.value.forEach((tag) => uniqueTags[tag.id] = tag);
  return uniqueTags;
});

async function loadShiftTags(date: NullableDateOptionType): Promise<void> {
  try {
    const response = await axios.get(`/api/shifts/tags?date=${toUniversalDate(date)}`);
    shiftTags.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('shift_tag.cannot_load'));
  }
}

function selectDefaultLocations() {
  // Selecting default locations, all except unwanted ones
  selectedLocations.value = availableLocations.value.filter((location) => (
    !getSetting('allow_location_selection')
    || ((props.user?.unwanted_locations ?? []).findIndex((unwantedLocation) => unwantedLocation.id === location.id) === -1)
  )).map((location) => location.id);
  handlePreSelectedSlot();
}

function updateSelectionAccordingToAvailability() {
  // Removing selected time slots that are not available anymore
  selectedTimeBands.value = selectedTimeBands.value.filter(
    (band) => (availableSlots.value.findIndex((availableSlot) => availableSlot.band.id === band) !== -1),
  );
  // Removing selected locations that are not available anymore
  selectedLocations.value = selectedLocations.value.filter(
    (location) => (availableLocations.value.findIndex((availableLocation) => availableLocation.id === location) !== -1),
  );
}

function updateAvailableLocations(updateSelection: boolean): void {
  // Remembering the currently available locations
  const oldAvailableLocations = availableLocations.value.map((location) => location.id);
  // Updating available locations
  availableLocations.value = [];
  availableSlots.value.forEach((slot) => {
    if (selectedTimeBands.value.includes(slot.band.id)) {
      slot.locations.forEach((slotLocation) => {
        if (availableLocations.value.findIndex((availableLocation) => availableLocation.id === slotLocation.id) === -1) {
          slotLocation.disabled = isLocationAssigned(slotLocation.id);
          availableLocations.value.push(slotLocation);
        }
      });
    }
  });
  // If we want to allow selection to be updated ...
  if (updateSelection) {
    // We only need to update the selection of the locations if
    // the available locations have actually chaged
    const newAvailableLocations = availableLocations.value.map((location) => location.id);
    if (didSelectionChange(newAvailableLocations, oldAvailableLocations)) {
      // Removing selected options that are not available anymore
      updateSelectionAccordingToAvailability();
      // Default location selection when no locations are selected
      selectDefaultLocations();
    }
  }
}

function cancel() {
  availabilityDialog.value = false;
  formDirty.value = false;
}

function checkFormValidity() {
  // Removing selected options that are not available anymore
  updateSelectionAccordingToAvailability();
  return (selectedTimeBandsValid.value && selectedLocationsValid.value);
}

async function handleSubmit(includeSpouse = false) {
  // If the form is valid ...
  if (checkFormValidity()) {
    // Attempt to save
    savingError.value = null;
    savingCount.value += 1;
    try {
      await axios.post(`/api/users/${props.user.id}/shifts/availability`, {
        avail_user: props.user.id,
        avail_date: toUniversalDate(selectedDate.value),
        avail_last_minute: lastMinute.value,
        avail_shifts_per_day: shiftsPerDay.value,
        avail_slots: availableSlots.value.filter((slot) => selectedTimeBands.value.includes(slot.band.id)).map((slot) => slot.id),
        avail_locations: selectedLocations.value,
        incl_spouse: includeSpouse,
        recurring_weeks: recurringWeeks.value,
      });
      savingCount.value -= 1;
      availabilityDialog.value = false;
      formDirty.value = false;
      showSnackMessage(editMode.value
        ? t('smpw.availability_updated')
        : t('smpw.availability_submitted'), 'success');
      emit('availability:updated', selectedDate.value);
    }
    catch (error) {
      savingError.value = axios.isAxiosError(error)
        ? error.response?.data?.message ?? null
        : null;
      savingCount.value -= 1;
    }
  }
}

function handlePreSelectedSlot() {
  if (props.preSelectedSlots.length === 0) {
    return;
  }
  props.preSelectedSlots.forEach((el) => {
    const shiftTimeBandId = el.shift_time_band_id;
    const locationId = el.location_id;
    if (!selectedTimeBands.value.includes(shiftTimeBandId)) {
      selectedTimeBands.value.push(shiftTimeBandId);
    }
    if (!selectedLocations.value.includes(locationId)) {
      selectedLocations.value.push(locationId);
    }
  });
}

async function loadAvailability(date: NullableDateOptionType): Promise<void> {
  loadingCount.value += 1;
  try {
    const slotsResponse = await axios.get(`/api/shifts/plan/day/${toUniversalDate(date)}`);
    availableSlots.value = slotsResponse.data.data;
    loadingCount.value -= 1;

    loadingCount.value += 1;
    const response = await axios.get(`/api/users/${props.user.id}/shifts/availability?date=${toUniversalDate(date)}`);
    // Updating available time slots from response

    // Updating assigned shift counts
    assignedShiftsThisDate.value = response.data.assigned_shifts_this_day;
    assignedShiftsThisWeek.value = response.data.assigned_shifts_this_week;
    availabilityResponse.value = response.data.shifts;

    // Updating selected time slots
    selectedTimeBands.value = availabilityResponse.value
      .filter((shift) => !isSameOrAfter(`${selectedDate.value} ${shift.begins}`))
      .map((shift) => shift.band.id)
      .filter((value, index, self) => self.indexOf(value) === index);

    // Updating selected locations
    selectedLocations.value = availabilityResponse.value
      .map((shift) => shift.location?.id)
      .filter((value, index, self) => self.indexOf(value) === index);
    assignedLocations.value = availabilityResponse.value
      .filter((shift) => shift.pivot.status === 'assigned' || shift.pivot.status === 'cancelled')
      .map((shift) => shift.location?.id)
      .filter((value, index, self) => self.indexOf(value) === index);

    // Updating selected options
    shiftsPerDay.value = response.data.shifts_per_day;
    lastMinute.value = response.data.available_last_minute;

    // Updating list of available locations
    updateAvailableLocations(false);

    // Applying pre selected slots
    handlePreSelectedSlot();
  }
  catch (error) {
    console.error(error);
    // No assigned shift loaded
    assignedShiftsThisDate.value = 0;
    assignedShiftsThisWeek.value = 0;

    // No time slots could be loaded
    availableSlots.value = [];

    // No selection loaded
    selectedTimeBands.value = [];
    selectedLocations.value = [];
    assignedLocations.value = [];
    lastMinute.value = true;
    shiftsPerDay.value = 1;

    // Updating list of available locations
    updateAvailableLocations(false);
    // Finished loading data
  }
  finally {
    loadingCount.value = 0;
  }
}

function updateAvailability(date: NullableDateOptionType): void {
  const validDateOrNull = getAsValidDateOrNull(date);
  const pictureOk = props.user.profile_image_status !== 'rejected';
  const trainingOk = props.user.training_completed || props.dontCheckTraining;
  const needsLinkedUsers = props.user.requires_linked_user_for_shift && (props.user.parent_users?.length ?? 0) === 0;
  if (!pictureOk) {
    let msg = '';
    if (authUser.id === props.user.id) {
      msg = t('smpw.submitting_availability_unavailable_image_me');
    }
    else {
      msg = t('smpw.submitting_availability_unavailable_image_user', { name: props.user.full_name });
    }
    showSnackMessage(msg, 'success');
  }
  else if (!trainingOk && props.user.type !== 'g' && !props.user.temporary_publisher) {
    let msg = '';
    if (authUser.id === props.user.id) {
      msg = t('smpw.submitting_availability_unavailable_training_me');
    }
    else {
      msg = t('smpw.submitting_availability_unavailable_training_user', { name: props.user.full_name });
    }
    showSnackMessage(msg, 'warning');
  }
  else if (needsLinkedUsers) {
    let msg = '';
    if (authUser.id === props.user.id) {
      msg = t('smpw.availability.requires_linked_user_me', {
        smpw: getSetting('smpw_acronym'),
      });
    }
    else {
      msg = t('smpw.availability.requires_linked_user_other', {
        name: props.user.full_name,
        smpw: getSetting('smpw_acronym'),
      });
    }
    showSnackMessage(msg, 'warning');
  }
  else {
    selectedDate.value = validDateOrNull;
    if (date) {
      loadAvailability(date);
      loadShiftTags(date);
    }
    availabilityDialog.value = true;
  }
}

watch(selectedTimeBands, async () => {
  if (!loadingCount.value) {
    updateAvailableLocations(true);
  }
});

watch(selectedLocations, async (newValue, oldValue) => {
  if (didSelectionChange(newValue, oldValue)) {
    if (newValue.length === 0) {
      someLocationsSelected.value = false;
      allLocationsSelected.value = false;
    }
    else if (newValue.length === availableLocations.value.length) {
      someLocationsSelected.value = false;
      allLocationsSelected.value = true;
    }
    else {
      someLocationsSelected.value = true;
      allLocationsSelected.value = false;
    }
  }
});

defineExpose({ updateAvailability });
</script>
